/**
 * Everything MusicXML wants before the first note: the titles, who wrote it, and the roster.
 *
 * `<part-list>` is the awkward part. The document holds a bracket as a group naming the
 * parts inside it; MusicXML holds it as a `start` marker before one `<score-part>` and a
 * `stop` after another, so a group has to be turned back into the pair of positions that
 * spans it. A group whose parts are not next to each other in score order cannot be said
 * in MusicXML at all, and is written as the span from its first member to its last, which
 * is the only reading of it the format has.
 */

import type { PartId } from "../document/ids";
import type { Part, Score, StaffGroup } from "../document/types";
import { TENTHS_PER_STAFF, tenthsOf } from "../document/layout";
import { el, leaf } from "../xml/write";
import { drumInstrumentNodes } from "./export-drums";
import { planInstruments } from "./export-instruments";
import type { PlayedInstrument } from "./export-instruments";
import type { XmlNode } from "../xml/write";

export interface HeaderOptions {
  /** What wrote the file, recorded in `<encoding>`. */
  readonly software?: string;
  /** `YYYY-MM-DD`; left out when not given, so exports stay byte-identical across runs. */
  readonly encodingDate?: string;
}

/**
 * A length as the file states it: tenths, rounded to four places.
 *
 * Four places is a tenth of a micrometre, which is finer than any printer and finer than
 * the numbers engravers write; the rounding is there so that a page whose size divides
 * badly by the staff height -- A4 rarely divides evenly -- comes back out of a file as the
 * same digits it went in as, rather than drifting by a float's last bit on every save.
 */
function tenths(millimetres: number, staffHeight: number): number {
  return Math.round(tenthsOf(millimetres, staffHeight) * 1e4) / 1e4;
}

/**
 * `<defaults>`: the page, its margins, and how large a staff stands on it.
 *
 * The `<tenths>` written is always 40, which makes the unit one fortieth of a staff and
 * the `<millimeters>` beside it the staff's own height. That is the reading every program
 * takes and the one the document is written in, so nothing has to be scaled twice.
 *
 * Nothing else an engraver could state is written: no fonts, no line widths, no default
 * positions. A renderer lays the music out from the notes, and carrying a previous
 * engraver's coordinates is what makes converted files an order of magnitude larger than
 * the music in them.
 */
export function defaultsNode(score: Score): XmlNode {
  const { layout } = score;
  const size = (millimetres: number): number => tenths(millimetres, layout.staffHeight);
  return el("defaults", {}, [
    el("scaling", {}, [leaf("millimeters", layout.staffHeight), leaf("tenths", TENTHS_PER_STAFF)]),
    el("page-layout", {}, [
      leaf("page-height", size(layout.pageHeight)),
      leaf("page-width", size(layout.pageWidth)),
      el("page-margins", { type: "both" }, [
        leaf("left-margin", size(layout.margins.left)),
        leaf("right-margin", size(layout.margins.right)),
        leaf("top-margin", size(layout.margins.top)),
        leaf("bottom-margin", size(layout.margins.bottom)),
      ]),
    ]),
    el("system-layout", {}, [leaf("system-distance", size(layout.systemGap))]),
    el("staff-layout", {}, [leaf("staff-distance", size(layout.staffGap))]),
  ]);
}

/** `<work>`, `<movement-*>` and `<identification>`, in the order the format requires. */
export function headerNodes(score: Score, options: HeaderOptions = {}): readonly XmlNode[] {
  const meta = score.meta;
  const creators = [
    meta.composer === null ? null : leaf("creator", meta.composer, { type: "composer" }),
    meta.lyricist === null ? null : leaf("creator", meta.lyricist, { type: "lyricist" }),
    meta.arranger === null ? null : leaf("creator", meta.arranger, { type: "arranger" }),
  ];

  const work = el("work", {}, [leaf("work-title", meta.title)]);
  const identification = el("identification", {}, [
    ...creators,
    meta.copyright === null ? null : leaf("rights", meta.copyright),
    el("encoding", {}, [
      options.encodingDate === undefined ? null : leaf("encoding-date", options.encodingDate),
      leaf("software", options.software ?? "score-editor"),
      el("supports", { element: "accidental", type: "yes" }),
      el("supports", { element: "transpose", type: "yes" }),
    ]),
  ]);

  // The order is the format's own: work, movement, identification, defaults, credits.
  return [
    work,
    meta.movementNumber === null ? null : leaf("movement-number", meta.movementNumber),
    meta.movementTitle === null ? null : leaf("movement-title", meta.movementTitle),
    identification,
    defaultsNode(score),
    meta.subtitle === null
      ? null
      : el("credit", { page: 1 }, [leaf("credit-words", meta.subtitle)]),
  ].filter((node): node is XmlNode => node !== null);
}

/** Where each group opens and closes, by the index of the part it happens at. */
function groupSpans(score: Score): ReadonlyMap<number, { open: number[]; close: number[] }> {
  const at = new Map<number, { open: number[]; close: number[] }>();
  const slot = (index: number): { open: number[]; close: number[] } => {
    const existing = at.get(index);
    if (existing !== undefined) return existing;
    const fresh = { open: [], close: [] };
    at.set(index, fresh);
    return fresh;
  };

  score.groups.forEach((group, number) => {
    const indexes = group.parts
      .map((id) => score.parts.findIndex((part) => part.id === id))
      .filter((index) => index >= 0);
    if (indexes.length === 0) return;
    slot(Math.min(...indexes)).open.push(number + 1);
    slot(Math.max(...indexes)).close.push(number + 1);
  });

  return at;
}

function groupNode(group: StaffGroup, number: number): XmlNode {
  return el("part-group", { type: "start", number }, [
    group.name === null ? null : leaf("group-name", group.name),
    leaf("group-symbol", group.symbol),
    leaf("group-barline", group.barlineSpan ? "yes" : "no"),
  ]);
}

/**
 * One instrument the part is played on: what it is called, and what it sounds like.
 *
 * The format wants every `<score-instrument>` before the first `<midi-instrument>`, so the
 * two halves are written in two passes rather than in pairs.
 */
function scoreInstrumentNode(played: PlayedInstrument): XmlNode {
  return el("score-instrument", { id: played.id }, [
    leaf("instrument-name", played.name),
    played.sound === null ? null : leaf("instrument-sound", played.sound),
  ]);
}

function midiInstrumentNode(played: PlayedInstrument): XmlNode | null {
  if (played.midiProgram === null) return null;
  return el("midi-instrument", { id: played.id }, [leaf("midi-program", played.midiProgram + 1)]);
}

function scorePartNode(score: Score, part: Part, id: string): XmlNode {
  // A drum part is a list of instruments rather than one: the kit says what each line of
  // the staff sounds, and a note points at the drum it is.
  const kit = part.instrument.percussion;
  // Everything the player picks up is listed, not only what they open on: a bar that hands
  // them another instrument names it by an id, and a file naming an id it never declared
  // is a broken file.
  const roster = planInstruments(score, part, id).played;
  const played =
    kit === null
      ? [...roster.map(scoreInstrumentNode), ...roster.map(midiInstrumentNode)]
      : drumInstrumentNodes(id, kit);

  return el("score-part", { id }, [
    leaf("part-name", part.name),
    part.abbreviation === "" ? null : leaf("part-abbreviation", part.abbreviation),
    ...played,
  ]);
}

/** The roster, with the brackets and braces wrapped around it. */
export function partListNode(score: Score, names: ReadonlyMap<PartId, string>): XmlNode {
  const spans = groupSpans(score);
  const children: XmlNode[] = [];

  score.parts.forEach((part, index) => {
    const here = spans.get(index);
    for (const number of here?.open ?? []) {
      const group = score.groups[number - 1];
      if (group !== undefined) children.push(groupNode(group, number));
    }
    children.push(scorePartNode(score, part, names.get(part.id) ?? `P${index + 1}`));
    for (const number of here?.close ?? []) {
      children.push(el("part-group", { type: "stop", number }));
    }
  });

  return el("part-list", {}, children);
}
